import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Play } from 'lucide-react';
import {
  MAINTENANCE_FREQUENCY_LABELS,
  formatCents,
  type MembershipFeeBlocked,
  type MembershipFeeLine,
  type MembershipFeeRun,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { currentCompetence } from '@/lib/queries';

/**
 * Manutenção periódica dos associados.
 *
 * O trabalho acontece sozinho: um job às 04:10 gera a competência do mês assim
 * que ela chega. Esta tela existe para as duas perguntas que o job não
 * responde — *"o que vai ser cobrado?"* antes de acontecer, e *"por que
 * fulano não entrou?"* depois.
 *
 * Gerar aqui é o mesmo que o job faz, e é IDEMPOTENTE: clicar duas vezes não
 * cobra o mês duas vezes.
 */

/**
 * O que a geração fez, incluindo o que ela NÃO conseguiu.
 *
 * Quando a emissão automática está ligada, a conta a receber e a cobrança são
 * dois passos: o segundo pode falhar sozinho (associado sem CPF, ASAAS fora
 * do ar) e a conta continua de pé. Esconder isso faria o operador acreditar
 * que o associado foi avisado quando ninguém mandou nada para ele.
 */
function ResultadoDaGeracao({ run }: { run: MembershipFeeRun }) {
  if (run.lines.length === 0) {
    return (
      <div className="alert alert--ok" role="status" style={{ marginBottom: 18 }}>
        Nada novo a gerar: estas competências já estavam lançadas.
      </div>
    );
  }

  const semCobranca = run.lines.filter((line) => line.chargeIssue !== null);
  const emitidas = run.lines.filter((line) => line.chargeId !== null).length;

  return (
    <div style={{ marginBottom: 18, display: 'grid', gap: 10 }}>
      <div className="alert alert--ok" role="status">
        {run.lines.length} lançamento(s) gerado(s), {formatCents(run.totalCents)} no contas a
        receber
        {emitidas > 0 ? ` · ${emitidas} cobrança(s) emitida(s)` : ''}.
      </div>

      {semCobranca.length > 0 && (
        <div className="alert alert--warn" role="alert">
          <strong>
            {semCobranca.length} lançamento(s) sem cobrança — a conta a receber existe, mas o
            associado não foi avisado.
          </strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            {semCobranca.map((line) => (
              <li key={`${line.memberId}-${line.competence}`}>
                {line.registration} · {line.memberName}: {line.chargeIssue}
              </li>
            ))}
          </ul>
          <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            Resolva o motivo e emita pela parcela, em Contas a receber. Gerar de novo aqui não
            repete a cobrança — a competência já está lançada.
          </p>
        </div>
      )}
    </div>
  );
}

/** `2026-09` vira `09/2026`, que é como o operador lê competência. */
function competenceLabel(competence: string): string {
  const [year, month] = competence.split('-');
  return `${month}/${year}`;
}

export function MensalidadesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [competenceTo, setCompetenceTo] = useState(currentCompetence());
  const [confirmando, setConfirmando] = useState(false);
  const [gerado, setGerado] = useState<MembershipFeeRun | null>(null);

  const query = useQuery({
    queryKey: ['membership-fees', 'preview', competenceTo],
    queryFn: () => api.get<MembershipFeeRun>('/membership-fees/preview', { competenceTo }),
  });

  const gerar = useMutation({
    mutationFn: () => api.post<MembershipFeeRun>('/membership-fees/generate', { competenceTo }),
    onSuccess: (result) => {
      setGerado(result);
      setConfirmando(false);
      void queryClient.invalidateQueries({ queryKey: ['membership-fees'] });
      // As manutenções nascem como conta a receber: a lista do Financeiro
      // precisa refletir isso sem o operador recarregar a página.
      void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
    },
  });

  const data = query.data;
  const lines = data?.lines ?? [];
  const blocked = data?.blocked ?? [];

  const columns: Array<Column<MembershipFeeLine & { id: string }>> = [
    { key: 'registration', label: 'Matrícula', mono: true, render: (row) => row.registration },
    { key: 'memberName', label: 'Associado', strong: true, render: (row) => row.memberName },
    { key: 'planName', label: 'Plano', render: (row) => row.planName },
    {
      key: 'frequency',
      label: 'Periodicidade',
      render: (row) => MAINTENANCE_FREQUENCY_LABELS[row.frequency],
    },
    {
      key: 'competence',
      label: 'Competência',
      mono: true,
      render: (row) => competenceLabel(row.competence),
    },
    { key: 'dueDate', label: 'Vencimento', mono: true, render: (row) => formatDate(row.dueDate) },
    {
      key: 'amountCents',
      label: 'Valor',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.amountCents),
    },
  ];

  const blockedColumns: Array<Column<MembershipFeeBlocked & { id: string }>> = [
    { key: 'registration', label: 'Matrícula', mono: true, render: (row) => row.registration },
    { key: 'memberName', label: 'Associado', strong: true, render: (row) => row.memberName },
    {
      key: 'competence',
      label: 'Competência',
      mono: true,
      render: (row) => competenceLabel(row.competence),
    },
    { key: 'reason', label: 'Por que não entrou', render: (row) => row.reason },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Mensalidades"
        actions={
          can('mensalidades:criar') && query.isSuccess && lines.length > 0 ? (
            <Button icon={Play} variant="primary" onClick={() => setConfirmando(true)}>
              Gerar {lines.length} lançamento{lines.length > 1 ? 's' : ''}
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <div className="filterbar" style={{ marginBottom: 18 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <span className="muted">Até a competência</span>
            <input
              type="month"
              className="input"
              value={competenceTo}
              max={currentCompetence()}
              onChange={(event) => setCompetenceTo(event.target.value || currentCompetence())}
              style={{ width: 160 }}
            />
          </label>
          <span className="muted" style={{ fontSize: 12.5 }}>
            A geração automática roda todo dia às 04:10 — aqui é para conferir antes ou correr
            atrás de um mês que ficou para trás.
          </span>
        </div>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {gerar.isError && (
          <div className="alert alert--bad" role="alert" style={{ marginBottom: 18 }}>
            {errorMessage(gerar.error)}
          </div>
        )}

        {gerado && <ResultadoDaGeracao run={gerado} />}

        {data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">A gerar</span>
                </div>
                <div className="metric__value">{lines.length}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Valor</span>
                </div>
                <div className="metric__value">{formatCents(data.totalCents)}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Impedidos</span>
                  {blocked.length > 0 && <AlertTriangle size={15} color={T.amber} aria-hidden />}
                </div>
                <div className={`metric__value${blocked.length > 0 ? ' metric__value--bad' : ''}`}>
                  {blocked.length}
                </div>
              </div>
            </div>

            {lines.length === 0 ? (
              <EmptyState
                title="Nenhuma manutenção em aberto"
                description={`Até ${competenceLabel(data.competenceTo)} está tudo lançado. Associado sem data de início da manutenção não entra na geração — isso se ajusta no cadastro dele.`}
              />
            ) : (
              <SmartDataTable
                caption="Manutenções em aberto"
                columns={columns}
                rows={lines.map((line) => ({ ...line, id: `${line.memberId}-${line.competence}` }))}
              />
            )}

            {blocked.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h2 style={{ fontSize: 14, marginBottom: 10 }}>Ficaram de fora</h2>
                <SmartDataTable
                  caption="Associados impedidos nesta geração"
                  columns={blockedColumns}
                  rows={blocked.map((row) => ({
                    ...row,
                    id: `${row.memberId}-${row.competence}`,
                  }))}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title="Gerar as manutenções?"
        description={`${lines.length} lançamento(s) de conta a receber, somando ${formatCents(data?.totalCents ?? 0)}, até a competência ${competenceLabel(competenceTo)}.`}
        footer={
          <>
            <Button onClick={() => setConfirmando(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => gerar.mutate()} disabled={gerar.isPending}>
              {gerar.isPending ? 'Gerando…' : 'Gerar'}
            </Button>
          </>
        }
      >
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Cada linha vira uma conta a receber no Financeiro, com vencimento no dia combinado com o
          associado. Se a <strong>cobrança automática</strong> estiver ligada em Configurações →
          Cobrança, cada uma já sai com o link de pagamento e o associado é avisado; senão, a
          cobrança sai da parcela, quando alguém decidir mandar.
        </p>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
          Gerar duas vezes não cobra duas vezes — a competência é única por associado.
        </p>
      </Modal>
    </>
  );
}
